import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "services/api";
import { get4rmLocal, remove4rmLocal, save2Local } from "store/localStore";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => get4rmLocal("auth_user"));
  const [token, setToken] = useState(() => get4rmLocal("auth_token"));

  const login = useCallback(async (email_address, password) => {
    const data = await api.post("/auth/login", { email_address, password, is_admin: true,});
    save2Local("auth_token", data.token);
    save2Local("auth_user", data.user);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const setupPassword = useCallback(async (token_param, password, password_confirmation) => {
    const data = await api.post("/auth/setup-password", { token: token_param, password, password_confirmation });
    save2Local("auth_token", data.token);
    save2Local("auth_user", data.user);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get("/user");
      const updated = data.user ?? data;
      save2Local("auth_user", updated);
      setUser(updated);
    } catch {}
  }, []);

  // Re-fetch the user when the tab becomes visible — picks up role changes made by another admin
  // without requiring a re-login.
  useEffect(() => {
    if (!token) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshUser();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [token, refreshUser]);

  const logout = useCallback(() => {
    remove4rmLocal("auth_token");
    remove4rmLocal("auth_user");
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated: !!token, login, setupPassword, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
